/* eslint-disable no-unused-vars */
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlagIcon from '@mui/icons-material/Flag';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
    alpha,
    Avatar,
    Box,
    Grid,
    LinearProgress,
    Paper,
    Stack,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';

const getPreviewUser = (userId, employeePreview) => {
    if (!userId || !Array.isArray(employeePreview)) return null;
    const found = employeePreview.find(u =>
        String(u.employeeId).toLowerCase() === String(userId).toLowerCase() ||
        (u.user && String(u.user.email).toLowerCase() === String(userId).toLowerCase())
    );
    return found;
};

const getFullName = (user) => {
    if (!user) return '';
    // Try to get full name from firstname, middlename, lastname
    const names = [user.firstname, user.middlename, user.lastname].filter(n => n && n.trim());
    if (names.length > 0) {
        return names.join(' ');
    }
    // Fallback to name property if available
    if (user.name && user.name.trim()) {
        return user.name.trim();
    }
    // Fallback to user.name if user object exists
    if (user.user && user.user.name && user.user.name.trim()) {
        return user.user.name.trim();
    }
    return '';
};

const GoalsOverview = ({ kpiDetails, employeePreview = [] }) => {
    const theme = useTheme();
    const { goals = [] } = kpiDetails;

    // Check if any scores exist across all goals
    const hasAnyScores = () => {
        return goals.some(goal => goal.scores && goal.scores.length > 0);
    };

    // Calculate average score for a goal from its scores array
    const calculateGoalAverage = (goal) => {
        if (!goal.scores || goal.scores.length === 0) return null; // Return null instead of 0
        const sum = goal.scores.reduce((acc, score) => acc + parseFloat(score.score || 0), 0);
        return sum / goal.scores.length;
    };

    // Group goals by category
    const groupGoalsByCategory = () => {
        const grouped = {};
        goals.forEach(goal => {
            const category = goal.category || 'Main';
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push(goal);
        });
        return grouped;
    };

    // Calculate category average (excluding goals with no evaluations)
    const calculateCategoryAverage = (categoryGoals) => {
        const goalsWithScores = categoryGoals.filter(goal => goal.scores && goal.scores.length > 0);
        if (goalsWithScores.length === 0) return null;
        
        let totalWeightedScore = 0;
        let totalWeight = 0;
        
        goalsWithScores.forEach(goal => {
            const avgScore = calculateGoalAverage(goal);
            const weight = parseFloat(goal.weight || 0);
            if (avgScore !== null) {
                totalWeightedScore += avgScore * weight;
                totalWeight += weight;
            }
        });
        
        return totalWeight > 0 ? totalWeightedScore / totalWeight : null;
    };

    const groupedGoals = groupGoalsByCategory();

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return <CheckCircleIcon color="success" />;
            case 'in_progress':
                return <RadioButtonUncheckedIcon color="primary" />;
            default:
                return <RadioButtonUncheckedIcon color="disabled" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return 'success';
            case 'in_progress':
                return 'primary';
            default:
                return 'default';
        }
    };

    const getWeightColor = (weight) => {
        const weightNum = parseFloat(weight || 0);
        if (weightNum <= 1) return theme.palette.error.main; // High weight - Red
        if (weightNum <= 2) return theme.palette.warning.main; // Medium-High - Orange
        if (weightNum <= 3) return theme.palette.warning.light; // Medium - Light Orange
        if (weightNum <= 4) return theme.palette.primary.main; // Low-Medium - Blue
        return theme.palette.success.main; // Low weight - Green
    };

    const getComplexityColor = (complexity) => {
        switch (complexity?.toLowerCase()) {
            case 'high':
                return theme.palette.error.main;
            case 'medium':
                return theme.palette.warning.main;
            case 'low':
                return theme.palette.success.main;
            default:
                return theme.palette.text.disabled;
        }
    };

    return (
        <Paper 
            elevation={0} 
            sx={{ 
                bgcolor: alpha(theme.palette.divider, 0.06),
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: 'hidden'
            }}
        >
            {/* Header */}
            <Box sx={{ p: 2.5, bgcolor: 'background.paper', borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1 }}>
                    <Box display="flex" alignItems="center" gap={1.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        <FlagIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                        <Box>
                            <Typography variant="h6" fontWeight={600}>
                                Goals Overview
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {goals.length} goal{goals.length !== 1 ? 's' : ''} across {Object.keys(groupedGoals).length} categor{Object.keys(groupedGoals).length !== 1 ? 'ies' : 'y'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ p: 2.5 }}>
                {goals.length === 0 ? (
                    <Box textAlign="center" py={6}>
                        <FlagIcon sx={{ fontSize: 56, color: theme.palette.text.disabled, mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" fontWeight={500}>
                            No goals assigned for this period
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Goals will appear here once they are assigned
                        </Typography>
                    </Box>
                ) : !hasAnyScores() ? (
                    <Box textAlign="center" py={6}>
                        <AccessTimeIcon sx={{ fontSize: 56, color: theme.palette.text.disabled, mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" fontWeight={500}>
                            Evaluation Not Started
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            No evaluations have been recorded yet
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={3}>
                        {Object.entries(groupedGoals).map(([category, categoryGoals]) => {
                            const categoryAverage = calculateCategoryAverage(categoryGoals);
                            const totalGoals = categoryGoals.length;
                            const evaluatedGoals = categoryGoals.filter(g => g.scores && g.scores.length > 0).length;
                            
                            return (
                                <Paper 
                                    key={category}
                                    elevation={0}
                                    sx={{ 
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        bgcolor: 'background.paper'
                                    }}
                                >
                                    {/* Category Header */}
                                    <Box 
                                        sx={{ 
                                            p: 2,
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                            bgcolor: 'background.paper'
                                        }}
                                    >
                                        <Grid container alignItems="center" spacing={1}>
                                            <Grid item xs={12} sm={8}>
                                                <Typography variant="h6" fontWeight={700} color="primary">
                                                    {category}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                    {totalGoals} goals &bull; {evaluatedGoals} evaluated
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: { xs: 'left', sm: 'right' }, fontWeight: 600 }}>
                                                        Category Avg
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight={700} color={categoryAverage !== null ? 'primary' : 'text.disabled'} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                                        {categoryAverage !== null ? `${categoryAverage.toFixed(1)}/5` : 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    {/* Goals Grid */}
                                    <Box sx={{ p: 2 }}>
                                        <Grid container spacing={2}>
                                            {categoryGoals.map((goal, index) => {
                                                const averageScore = calculateGoalAverage(goal);
                                                const complexity = (goal.goal?.complexity || goal.complexity || 'medium').toLowerCase();
                                                const status = (goal.status || goal.goal?.status || 'not_started').toLowerCase();
                                                const weight = parseFloat(goal.weight || 0);
                                                const hasScores = goal.scores && goal.scores.length > 0;
                                                
                                                // Determine score color
                                                const getScoreColor = (score) => {
                                                    if (score === null) return theme.palette.text.disabled;
                                                    if (score >= 4) return theme.palette.success.main;
                                                    if (score >= 3) return theme.palette.success.dark;
                                                    if (score >= 2) return theme.palette.warning.main;
                                                    return theme.palette.error.main;
                                                };
                                                
                                                const scoreColor = getScoreColor(averageScore);
                                                
                                                return (
                                                    <Grid item xs={12} sm={6} lg={4} key={goal.id || index}>
                                                        <Paper
                                                            elevation={0}
                                                            sx={{
                                                                width: '100%',
                                                                height: '100%',
                                                                border: `1px solid ${theme.palette.divider}`,
                                                                borderRadius: 2,
                                                                overflow: 'hidden',
                                                                bgcolor: 'background.paper',
                                                                transition: 'all 0.2s ease'
                                                                // Removed hover effect
                                                            }}
                                                        >
                                                            <Box sx={{ p: 2 }}>
                                                                {/* Header with Status (only if no scores) and Evaluation Count Badge */}
                                                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                                                                    {/* Removed Not Started text */}
                                                                    <Box />
                                                                </Box>

                                                                {/* Goal Title */}
                                                                <Tooltip title={goal.goal?.name || goal.name || `Goal ${index + 1}`}>
                                                                    <Typography 
                                                                        variant="subtitle1" 
                                                                        fontWeight={600}
                                                                        sx={{
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            display: '-webkit-box',
                                                                            WebkitLineClamp: 2,
                                                                            WebkitBoxOrient: 'vertical',
                                                                            lineHeight: 1.4
                                                                        }}
                                                                    >
                                                                        {goal.goal?.name || goal.name || `Goal ${index + 1}`}
                                                                    </Typography>
                                                                </Tooltip>

                                                                {/* Goal Description */}
                                                                {(goal.goal?.description || goal.description) && (
                                                                    <Tooltip title={goal.goal?.description || goal.description} arrow placement="bottom-start">
                                                                        <Typography 
                                                                            variant="caption" 
                                                                            color="text.secondary"
                                                                            sx={{
                                                                                display: 'block',
                                                                                mt: 0.5,
                                                                                mb: 2,
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                whiteSpace: 'nowrap',
                                                                                maxWidth: '100%',
                                                                                cursor: 'pointer'
                                                                            }}
                                                                        >
                                                                            {goal.goal?.description || goal.description}
                                                                        </Typography>
                                                                    </Tooltip>
                                                                )}

                                                                {/* Score Display - Centered */}
                                                                <Box sx={{ textAlign: 'center', my: 2 }}>
                                                                    <Box display="flex" alignItems="baseline" justifyContent="center" gap={0.5} mb={1}>
                                                                        {averageScore !== null ? (
                                                                            <>
                                                                                <Typography 
                                                                                    variant="h3" 
                                                                                    fontWeight={700}
                                                                                    sx={{ 
                                                                                        color: scoreColor,
                                                                                        lineHeight: 1
                                                                                    }}
                                                                                >
                                                                                    {averageScore.toFixed(1)}
                                                                                </Typography>
                                                                                <Typography variant="h6" color="text.secondary">
                                                                                    /5
                                                                                </Typography>
                                                                            </>
                                                                        ) : (
                                                                            <Typography variant="h3" fontWeight={700} color="text.disabled">
                                                                                N/A
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                                                        Average Score
                                                                    </Typography>
                                                                    {averageScore !== null && (
                                                                        <LinearProgress
                                                                            variant="determinate"
                                                                            value={(averageScore / 5) * 100}
                                                                            sx={{
                                                                                height: 6,
                                                                                borderRadius: 3,
                                                                                backgroundColor: alpha(scoreColor, 0.15),
                                                                                '& .MuiLinearProgress-bar': {
                                                                                    backgroundColor: scoreColor,
                                                                                    borderRadius: 3
                                                                                }
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Box>

                                                                {/* Metrics - 2 Column */}
                                                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                                                    <Grid item xs={6}>
                                                                        <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 1.5, bgcolor: 'transparent', border: `1px solid ${theme.palette.divider}` }}>
                                                                            <Typography variant="h5" fontWeight={700}>
                                                                                {typeof goal.weight !== 'undefined' ? parseFloat(goal.weight).toFixed(0) : '-'}
                                                                            </Typography>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Weight
                                                                            </Typography>
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={6}>
                                                                        <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 1.5, bgcolor: 'transparent', border: `1px solid ${theme.palette.divider}` }}>
                                                                            <Typography variant="h5" fontWeight={700}>
                                                                                {goal.scores?.length || 0}
                                                                            </Typography>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Evaluations
                                                                            </Typography>
                                                                        </Box>
                                                                    </Grid>
                                                                </Grid>
                                                                {!hasScores && (
                                                                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                        <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, px: 2, py: 1, display: 'flex', alignItems: 'center', bgcolor: 'background.paper', width: '100%' }}>
                                                                            <InfoOutlinedIcon sx={{ color: theme.palette.primary.main, fontSize: 18, mr: 1 }} />
                                                                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                                                                No scoring yet. This goal does not affect the overall score.
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                )}

                                                                {/* Score History Section - Scrollable */}
                                                                {hasScores && (
                                                                    <Box sx={{ bgcolor: alpha(theme.palette.divider, 0.06), borderRadius: 1.5, p: 1.5 }}>
                                                                        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                                                                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                                                                                Score History
                                                                                <span style={{ marginLeft: 8, color: 'inherit', fontWeight: 600 }}>
                                                                                    ({goal.scores?.length || 0})
                                                                                </span>
                                                                            </Typography>
                                                                        </Box>
                                                                        <Stack spacing={1} sx={{
                                                                            maxHeight: '160px',
                                                                            overflowY: 'auto',
                                                                            pr: 0.5,
                                                                            '&::-webkit-scrollbar': {
                                                                                display: 'none'
                                                                            },
                                                                            scrollbarWidth: 'none',
                                                                            '-ms-overflow-style': 'none'
                                                                        }}>
                                                                            {goal.scores.map((score, idx) => {
                                                                                const userId = score.updatedBy || score.createdBy;
                                                                                const userInfo = getPreviewUser(userId, employeePreview);
                                                                                const fullName = getFullName(userInfo);
                                                                                const scoreValue = parseFloat(score.score || 0);
                                                                                const scoreItemColor = getScoreColor(scoreValue);
                                                                                const hasRemark = score.remarks || score.remark || score.comments;
                                                                                // Use scoreDate if available, otherwise fall back to updatedAt
                                                                                const displayDate = score.scoreDate || score.updatedAt;
                                                                                
                                                                                return (
                                                                                    <Box
                                                                                        key={idx}
                                                                                        sx={{
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            gap: 1,
                                                                                            py: 0.75
                                                                                        }}
                                                                                    >
                                                                                        <Avatar
                                                                                            src={userInfo?.user?.profilePicture}
                                                                                            sx={{ width: 32, height: 32 }}
                                                                                        >
                                                                                            {(fullName || userId || 'U').charAt(0).toUpperCase()}
                                                                                        </Avatar>
                                                                                        <Box flex={1} minWidth={0}>
                                                                                            <Typography variant="caption" fontWeight={600} display="block" noWrap>
                                                                                                {fullName || userId || 'Unknown'}
                                                                                            </Typography>
                                                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                                                                {displayDate ? new Date(displayDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : 'No Date'}
                                                                                            </Typography>
                                                                                        </Box>
                                                                                        <Box display="flex" alignItems="center" gap={0.5}>
                                                                                            <Typography
                                                                                                variant="caption"
                                                                                                sx={{
                                                                                                    display: 'inline-block',
                                                                                                    backgroundColor: scoreItemColor,
                                                                                                    color: (theme) => theme.palette.getContrastText(scoreItemColor),
                                                                                                    fontWeight: 700,
                                                                                                    fontSize: '0.75rem',
                                                                                                    minWidth: 50,
                                                                                                    textAlign: 'center',
                                                                                                    borderRadius: 1,
                                                                                                    px: 1,
                                                                                                    py: 0.5
                                                                                                }}
                                                                                            >
                                                                                                {`${scoreValue}/5`}
                                                                                            </Typography>
                                                                                            {hasRemark && (
                                                                                                <Tooltip 
                                                                                                    title={
                                                                                                        <Box sx={{ p: 0.5, maxWidth: 250 }}>
                                                                                                            <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                                                                                                                Remark:
                                                                                                            </Typography>
                                                                                                            <Typography variant="caption">
                                                                                                                "{hasRemark}"
                                                                                                            </Typography>
                                                                                                        </Box>
                                                                                                    }
                                                                                                    arrow
                                                                                                >
                                                                                                    <InfoOutlinedIcon 
                                                                                                        sx={{ 
                                                                                                            fontSize: 16, 
                                                                                                            color: theme.palette.primary.main,
                                                                                                            cursor: 'pointer'
                                                                                                        }} 
                                                                                                    />
                                                                                                </Tooltip>
                                                                                            )}
                                                                                        </Box>
                                                                                    </Box>
                                                                                );
                                                                            })}
                                                                        </Stack>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </Paper>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Stack>
                )}
            </Box>
        </Paper>
    );
};

export default GoalsOverview;
